import { Injectable, OnDestroy } from '@angular/core';

import { OfferSource } from '@cds';
import { DisplayType } from '@cds/betting-offer';
import { GameUpdateCommand, OptionMarketUpdateCommand } from '@cds/push/fixture-commands';
import { DetailedFixtureFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel, EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { TeaserBetConfig } from '@frontend/sports/common/client-config-data-access';
import { Store } from '@ngrx/store';
import { Subscription, map } from 'rxjs';

import { EventDetailsService } from '../../../../lib/event-details-common/event-details.service';
import { OptionGroupService } from '../../../../lib/option-group/option-group.service';
import { IBetslipRootState, IBetslipState } from '../../../base/store/state';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { BetslipV2OptionMarketPick } from '../../../core/picks/betslip-v2-option-market-pick';
import { BetslipTeaserActions } from '../actions';
import { ITeaserBetPickState } from '../state';

@Injectable()
export class TeaserBetFilterService implements OnDestroy {
    private teaserPickEvents: Map<string, EventModel> = new Map<string, EventModel>();
    private subscription = new Subscription();

    constructor(
        private teaserBetConfig: TeaserBetConfig,
        private optionGroupService: OptionGroupService,
        private fixtureFactory: DetailedFixtureFactory,
        private groupingService: OfferGroupingService,
        private store$: Store<IBetslipRootState>,
        private eventDetailsService: EventDetailsService,
    ) {}

    isValidV1Pick(pick: BetslipV1Pick, betslipState: IBetslipState): boolean {
        return !pick.isLive && this.isValidV1PickForTeaserBet(pick, betslipState);
    }

    isValidV2Pick(pick: BetslipV2OptionMarketPick, betslipState: IBetslipState): boolean {
        return !pick.isLive && this.isValidV2PickForTeaserBet(pick, betslipState);
    }

    updateEvent(eventId: string, command: GameUpdateCommand | OptionMarketUpdateCommand, teaserPicks: ITeaserBetPickState[]) {
        const event = this.teaserPickEvents.get(eventId);

        if (!event) {
            return;
        }

        this.subscription = this.groupingService
            .getFixtureGroupingForEventModel(event)
            .pipe(
                map((grouping) => {
                    const optionGroup = 'game' in command ? command.game : command.optionMarket;
                    this.fixtureFactory.updateOptionGroup(event, optionGroup, command.fixtureId, grouping);

                    return event;
                }),
            )
            .subscribe((eventModel) => {
                if (eventModel) {
                    teaserPicks?.forEach((teaserPick) => {
                        const eventOption = eventModel.optionGroups
                            .flatMap((group) => group.options)
                            .find((o) => o.id === teaserPick?.selectedOption?.id);
                        const isNonBalanceMarket = !this.isBalancedOption(eventModel, eventOption!);

                        if (teaserPick.isNonBalancedPick !== isNonBalanceMarket) {
                            this.store$.dispatch(
                                BetslipTeaserActions.updateBalancePick({
                                    payload: {
                                        pickId: teaserPick.id,
                                        isNonBalanceMarket,
                                    },
                                }),
                            );
                        }
                    });
                }
            });
    }

    isBalancedOption(event: EventModel, eventOption: EventOption): boolean {
        if (event.offerSource === OfferSource.V1) {
            const validTemplateIds = this.isValidTemplateIdsForTeasers(event.sport.id, eventOption.templateId!);
            if (!validTemplateIds) {
                return false;
            }
        }

        this.teaserPickEvents.set(event.id, event);
        const optionGroups = this.eventDetailsService.prepareEventOptionGroups({ event });
        const sixPackGroups = optionGroups.filter(
            (group) =>
                group.detailedGrouping.displayType === DisplayType.SixPack || group.detailedGrouping.displayType === DisplayType.Head2HeadSixPack,
        );
        const sixPackGroup = this.getOptionGroupHavingPick(sixPackGroups, eventOption.id);

        if (sixPackGroup) {
            const balancedOptionGroups = this.optionGroupService.getOptionGroupsFilteredBySpread(sixPackGroup, (option) => option.optionColumnId);
            const balancedOptionGroup = balancedOptionGroups.find((og) => og.options.find((o) => o.id === eventOption.id));

            if (balancedOptionGroup) {
                return true;
            }
        }

        return false;
    }

    isValidTemplateIdsForTeasers(sportId: number, templateId: number): boolean {
        return !!this.teaserBetConfig.allowedTemplateIds.find((x) => x.sportId === sportId)?.templateIds.some((t) => t === templateId);
    }

    private isValidV2PickForTeaserBet(pick: BetslipV2OptionMarketPick, betslipState: IBetslipState): boolean {
        //The goal is (possibly) to allow teasers cross tv1/tv2 - however, until tca, basv2 etc. are all ready
        //bet placement will fail. -> remove v1TeaserPicks check once it's fixed in dependencies.
        const v1TeaserPicks = betslipState.picks.pickList.some(
            (betslipPick) => BetslipV1Pick.isPick(betslipPick) && !!betslipState.types.teaserBet.picks[betslipPick.id.toString()],
        );
        return !v1TeaserPicks && !!pick.market.isTeasable;
    }

    private isValidV1PickForTeaserBet(pick: BetslipV1Pick, betslipState: IBetslipState): boolean {
        if (!pick.teaser?.crossLeagueIds?.length || !pick.teaser?.crossSportIds?.length) {
            return false;
        }

        const validTemplateIds = this.isValidTemplateIdsForTeasers(pick.sportId, pick.market.templateId);
        if (!validTemplateIds) {
            return false;
        }

        //The goal is (possibly) to allow teasers cross tv1/tv2 - however, until tca, basv2 etc. are all ready
        //bet placement will fail. -> remove v2TeaserPicks check once it's fixed in dependencies.
        const v2TeaserPicks = betslipState.picks.pickList.some(
            (betslipPick) => BetslipV2OptionMarketPick.isPick(betslipPick) && !!betslipState.types.teaserBet.picks[betslipPick.id.toString()],
        );

        if (v2TeaserPicks) {
            return false;
        }

        //TODO: add a BetslipV1Pick.isPick(betslipPick) inside the teaserpicks filter.
        //note - that will break unittests due to the way data is mocked.
        const teaserPicks =
            (
                betslipState.picks.pickList.filter(
                    (betslipPick) => !!betslipState.types.teaserBet.picks[betslipPick.id.toString()],
                ) as BetslipV1Pick[]
            ).map((pick) => {
                return {
                    sportId: pick.sport.id,
                    leagueId: pick.league.id,
                    crossLeagueIds: [...pick.teaser.crossLeagueIds, pick.league.id],
                    crossSportIds: [...pick.teaser.crossSportIds, pick.sport.id],
                };
            }) || [];

        const payloadInfo = {
            sportId: pick.sportId,
            leagueId: pick.league.id,
            crossLeagueIds: [...pick.teaser.crossLeagueIds, pick.league.id],
            crossSportIds: [...pick.teaser.crossSportIds, pick.sportId],
        };

        return (
            pick.teaser?.crossLeagueIds?.length > 0 &&
            pick.teaser?.crossSportIds?.length > 0 &&
            (!teaserPicks.length ||
                teaserPicks.every(
                    (pick) =>
                        pick.crossLeagueIds.includes(payloadInfo.leagueId) &&
                        pick.crossSportIds.includes(payloadInfo.sportId) &&
                        payloadInfo.crossSportIds.includes(pick.sportId) &&
                        payloadInfo.crossLeagueIds.includes(pick.leagueId),
                ))
        );
    }

    private getOptionGroupHavingPick(optionGroups: EventOptionGroup[], optionId: number): EventOptionGroup | undefined {
        for (const group of optionGroups) {
            if (group.subGroups?.length) {
                const subgroup = group.subGroups.find((sg) => sg.options.some((o) => o.id === optionId));
                if (subgroup) {
                    return subgroup;
                }
            }

            if (group.options.some((o) => o.id === optionId)) {
                return group;
            }
        }

        return undefined;
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }
}
